import type React from 'react';
import { useState, useRef, useEffect } from 'react';
import { useAppDispatch } from '../../app/hooks';
import { Select } from '@digital/react-aviva-atom';
import classNames from 'classnames';
import { sortBy } from 'lodash';
import type { RootState } from '../../app/store';
import { setSelectedLocation } from '../../redux/user-selection/userSelection';
import { getFirstAvailability, resetAvailability } from '../../redux/availability/availability';
import { useAppSelector } from '../../app/hooks';
import type { ActionMeta } from 'react-select';

import './PickupLocation.css';
interface OwnProps {
  onSearch: (state: State) => void;
  resetAvailableTime: () => void;
}

export type Props = OwnProps;

interface State {
  locationInfo?: endpoints.Location;
  county?: string;
  area?: string;
  areas?: { label: string; value: string }[] | undefined;
  localities?: { label: string; value: string; locationInfo?: endpoints.Location }[] | undefined;
  location?: string;
  isSearchable: boolean;
}

export enum FieldNames {
  county = 'county',
  area = 'area',
  location = 'location',
}

const PickupLocation: React.FC<OwnProps> = ({ onSearch, resetAvailableTime }) => {
  const dispatch = useAppDispatch();
  const locations = useAppSelector((state) => state.pickup.locations);
  const locationLoading = useAppSelector((state: RootState) => state.pickup.loading);

  const [state, setState] = useState<State>({
    county: undefined,
    area: undefined,
    areas: undefined,
    location: undefined,
    isSearchable: false,
    locationInfo: undefined,
  });

  // Ref to store the previous location
  const prevLocationRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Compare the current location with the previous location
    if (prevLocationRef.current !== state.location) {
      resetAvailableTime();
    }

    // Update the ref to the current location after the check
    prevLocationRef.current = state.location;
  }, [state.location, resetAvailableTime]);

  const onCountyChange = (newValue: unknown, _actionMeta: ActionMeta<unknown>) => {
    const event = newValue as { label: string; value: string } | null;
    const county = event ? event.value : undefined;
    let sortedAreas = undefined;
    if (county && locations[county]) {
      const areas = Object.keys(locations[county]).map((key) => ({
        label: key,
        value: key,
      }));
      sortedAreas = sortBy(areas, (area) => area.label);
    }
    dispatch(resetAvailability());
    setState({
      ...state,
      county: county,
      areas: sortedAreas,
      area: undefined,
      location: undefined,
      isSearchable: false,
      locationInfo: undefined,
    });
  };

  const onAreaChange = (newValue: unknown, _actionMeta: ActionMeta<unknown>) => {
    const event = newValue as { label: string; value: string } | null;
    const area = event ? event.value : undefined;
    let sortedLocalities = undefined;
    if (area && state.county) {
      const localities: { label: string; value: string; locationInfo: endpoints.Location }[] = locations[state.county][area].map((val: endpoints.Location) => ({
        label: val.locality,
        value: val.locality,
        locationInfo: val,
      }));
      sortedLocalities = sortBy(localities, (locality) => locality.label);
    }
    dispatch(resetAvailability());
    setState({
      ...state,
      area,
      localities: sortedLocalities,
      location: undefined,
      isSearchable: false,
      locationInfo: undefined,
    });
  };

  const onLocationChange = (newValue: unknown, _actionMeta: ActionMeta<unknown>) => {
    const event = newValue as { label: string; value: string; locationInfo: endpoints.Location } | null;
    const location = event ? event.value : undefined;
    const search = !!location;
    const locInfo = event ? event.locationInfo : undefined;
    dispatch(resetAvailability());
    setState({
      ...state,
      location,
      isSearchable: search,
      locationInfo: locInfo,
    });
  };
  
  const onClick = () => {
    const { locationInfo } = state;
    setState({ ...state, isSearchable: false });
    onSearch(state);
    if (locationInfo) {
      dispatch(setSelectedLocation({ location: locationInfo }));
      dispatch(getFirstAvailability(locationInfo.geoareaId));
    }
  };

  const counties = Object.keys(locations).map((key) => ({ label: key, value: key }));
  const sortedCounties = sortBy(counties, (county) => county.label);

  const styles = {control: (base: any) => ({
                    ...base,
                    border: '1px solid #aaa',
                    height: '100%',
                    minHeight:'3.125em',
                    padding:0,
                    appearance: 'none',
                    backgroundColor:'#f9f9f9',
                    borderRadius:'.25em',
                    color: '#444',
                    lineHeight: '1.5em',
                    maxWidth: '100%'
                })
              }

  const searchBtnClasses = classNames(
    'a-button',
    'a-button--primary',
    'a-button--full-width',
    { 'is-disabled': !state.isSearchable }
  );

  return (
    <div data-testid="pickup-location">
      <div className="m-form-row">
        <label className="a-label" htmlFor="select-county" data-testid="select-county-label">
          Select County
        </label>
        <Select
          className="pickup-select"
          options={sortedCounties}
          value={state.county ? { label: state.county, value: state.county } : null}
          onChange={onCountyChange}
          isLoading={locationLoading}
          isDisabled={locationLoading}
          placeholder="County"
          data-testid="select-county"
          inputId="select-county"
          styles={styles}
        />
      </div>
      <div className="m-form-row">
        <label className="a-label" htmlFor="select-area" data-testid="select-area-label">
          Select Area
        </label>
        <Select
          className="pickup-select"
          options={state.areas}
          value={state.area ? { label: state.area, value: state.area } : null}
          onChange={onAreaChange}
          isDisabled={!state.county}
          placeholder="Area"
          data-testid="select-area"
          inputId="select-area"
          styles={styles}
        />
      </div>
      <div className="m-form-row">
        <label className="a-label" htmlFor="select-pickup-location" data-testid="select-pickup-label">
          Select Pickup Location
        </label>
        <Select
          className="pickup-select"
          options={state.localities}
          value={state.location ? { label: state.location, value: state.location } : null}
          onChange={onLocationChange}
          isDisabled={!state.area}
          placeholder="Pickup location"    
          data-testid="select-pickup-location"
          inputId="select-pickup-location"
          styles={styles}
        />
      </div>
      <div className="m-form-row button-center">
        <button className={searchBtnClasses} onClick={onClick} data-testid="search-button" disabled={!state.isSearchable}>
          Search
        </button>
      </div>
    </div>
  );
};

export default PickupLocation;